import React, { useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useLanguage, translateDynamic } from '../../context/LanguageContext';
import {
    generateReceiptHTML,
    printWithIframe
} from '../../utils/PrintingUtils';

const PendingOrderCard = ({
    tableKey,
    order,
    isPrinted,
    currency,
    printableCategories: externalPC,
    onEdit,
    onPrint,
    onClear,
    onCancelPrint,
    onChangeTable,
    onSubmit,
    onDelete,
    onMarkServed,
    occupation,
    setSuccess,
    setError
}) => {
    const { tenantConfig, getBrandName } = useAuth();
    const { t } = useLanguage();
    const brandName = getBrandName();

    const handleChangeTable = () => {
        onChangeTable(tableKey);
    };

    const handleDelete = () => {
        onDelete(tableKey);
    };

    const handlePrint = async () => {
        if (!order) return;

        const receiptData = {
            date: order.date,
            time: order.time,
            cashier: order.cashier,
            paymentMethod: order.paymentMethod,
            diningOption: order.diningOption,
            tableNumber: order.tableNumber,
            products: order.products,
            subtotal: order.subtotal,
            discount: order.discount,
            serviceCharge: order.serviceCharge,
            total: order.total,
            notes: order.notes || t('preview_not_finalized')
        };

        const tenantInfo = {
            brand_name: brandName,
            currency,
            printable_categories: externalPC || [],
            receipt_header: tenantConfig?.receipt_header,
            receipt_footer: tenantConfig?.receipt_footer,
            receipt_logo: tenantConfig?.receipt_logo
        };

        const receiptHTML = generateReceiptHTML(
            { ...receiptData, notes: receiptData.notes + ` (${t('preview_only_not_paid')})` },
            t('receipt_preview').toUpperCase(),
            false,
            tenantInfo
        );

        const previewHTML = `
            <div class="print-page">
                <div class="preview-banner" style="background-color: #fef2f2; color: #b91c1c; text-align: center; padding: 4px 0; font-weight: bold; margin: 8px 0; border: 1px dashed #b91c1c;">
                    ${t('preview_only_not_paid')}
                </div>
                ${receiptHTML}
                <div class="preview-banner" style="background-color: #fef2f2; color: #b91c1c; text-align: center; padding: 4px 0; font-weight: bold; margin: 8px 0; border: 1px dashed #b91c1c;">
                    ${t('preview_only_not_paid')}
                </div>
            </div>
        `;

        printWithIframe(previewHTML, `${brandName.toUpperCase()} ${t('receipt_preview')}`, tenantConfig?.printing?.method, tenantConfig);

        await onPrint(tableKey);
        setSuccess(t('preview_receipt_printed'));
        setTimeout(() => setSuccess(''), 1000);
    };

    return (
        <>
            <div className={`border rounded-lg p-4 ${isPrinted ? 'bg-purple-200 border-purple-400' : 'bg-gray-50'}`}>
                <div className="flex justify-between items-center mb-2">
                    <h3 className={`font-medium ${isPrinted ? 'text-purple-800' : ''}`}>
                        {order.diningOption}: {order.tableNumber}
                        {isPrinted && <span className="text-xs ml-1">({t('printed')})</span>}
                    </h3>
                    <span className="text-sm text-gray-500">{order.time}</span>
                </div>

                <div className="mb-2">
                    <p className="text-sm text-gray-600">
                        {order.products.length} {t('items')} · {currency}{order.total.toFixed(2)}
                    </p>
                    <div className="text-sm text-gray-600 mt-1">
                        {order.products.slice(0, 2).map((product, idx) => (
                            <div key={idx} className="truncate">
                                {product.quantity}x {product.name}
                            </div>
                        ))}
                        {order.products.length > 2 && (
                            <div className="relative group">
                                <div className="text-gray-500 cursor-pointer">
                                    + {order.products.length - 2} more items
                                </div>
                                <div className="absolute z-10 hidden group-hover:block bg-white border border-gray-200 shadow-lg rounded-md p-2 w-64 left-0">
                                    <div className="text-sm font-medium mb-1">{t('all_products_colon')}</div>
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                        {order.products.map((product, idx) => (
                                            <div key={idx} className="text-sm">
                                                {product.quantity}x {product.name}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {occupation && (
                    <div className="mb-2 flex items-center gap-2 text-xs">
                        {occupation.served_at ? (
                            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800">{t('served') || 'Served'}</span>
                        ) : (
                            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{t('seated') || 'Seated'}</span>
                        )}
                        {occupation.bill_requested_at && (
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{t('bill_requested') || 'Bill Requested'}</span>
                        )}
                    </div>
                )}

                {order.paid && (
                    <div className="mb-2">
                        <span data-testid="pending-card-paid" className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-bold">
                            {t('paid') || 'Paid'}
                        </span>
                    </div>
                )}

                {(() => {
                    const isServed = occupation?.served_at;
                    const isBillRequested = isPrinted || occupation?.bill_requested_at;

                    // A PAID order has already been settled — it is waiting to
                    // be handed to the customer, nothing more (client request
                    // 2026-08-26). Submit would take the money a second time
                    // and Edit would desynchronise the items from the
                    // transaction, so neither is offered; Clear completes it.
                    if (order.paid) {
                        return (<>
                            <div className="flex space-x-2">
                                <button onClick={handlePrint} className="flex-1 py-1 px-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm">{t('print_receipt') || 'Print Receipt'}</button>
                                <button onClick={handleChangeTable} className="flex-1 py-1 px-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">{t('table')}</button>
                            </div>
                            <div className="flex space-x-2 mt-2">
                                <button
                                    data-testid="pending-card-clear"
                                    onClick={() => onClear && onClear(tableKey)}
                                    className="flex-1 py-1 px-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                                >
                                    {t('clear_order') || 'Clear'}
                                </button>
                                <button onClick={handleDelete} className="flex-1 py-1 px-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm">{t('delete')}</button>
                            </div>
                        </>);
                    }

                    if (isBillRequested) {
                        return (<>
                            <div className="flex space-x-2">
                                <button onClick={handlePrint} className="flex-1 py-1 px-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm">{t('print_receipt') || 'Print Receipt'}</button>
                                <button onClick={() => onEdit(tableKey)} className="flex-1 py-1 px-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm">{t('edit')}</button>
                            </div>
                            <div className="flex space-x-2 mt-2">
                                <button onClick={() => onSubmit(tableKey)} className="flex-1 py-1 px-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm">{t('submit')}</button>
                            </div>
                        </>);
                    }

                    return (<>
                        <div className="flex space-x-2">
                            <button onClick={() => onEdit(tableKey)} className="flex-1 py-1 px-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm">{t('edit')}</button>
                            {occupation && !isServed && onMarkServed && (
                                <button onClick={() => onMarkServed(tableKey)} className="flex-1 py-1 px-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">{t('mark_served') || 'Mark Served'}</button>
                            )}
                        </div>
                        <div className="flex space-x-2 mt-2">
                            <button onClick={handlePrint} className="flex-1 py-1 px-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm">{t('request_bill') || 'Request Bill'}</button>
                            <button onClick={handleChangeTable} className="flex-1 py-1 px-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">{t('table')}</button>
                        </div>
                        <div className="flex space-x-2 mt-2">
                            <button onClick={() => onSubmit(tableKey)} className="flex-1 py-1 px-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm">{t('submit')}</button>
                            <button onClick={handleDelete} className="flex-1 py-1 px-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm">{t('delete')}</button>
                        </div>
                    </>);
                })()}
            </div>
        </>
    );
};

export default PendingOrderCard;